import { useState, useEffect } from "react";
import { Link, useSearch } from "wouter";
import { Search, ArrowLeft, MapPin, Map, SortAsc } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";

import { RouteCard } from "@/components/RouteCard";
import { LoginModal } from "@/components/LoginModal";
import { useAuth } from "@/lib/authContext";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { PROVINCES } from "@shared/schema";
import { EUROPE_COUNTRIES } from "@shared/europe-countries";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/lib/supabase";
import { useI18n } from "@/lib/i18n";

type SortOption = "popular" | "new" | "saves" | "shops" | "distance";

export default function SearchRoutesPage() {
    const searchParams = new URLSearchParams(useSearch());
    const { isLoggedIn } = useAuth();
    const { t } = useI18n();
    const [showLoginModal, setShowLoginModal] = useState(false);
    const { toast } = useToast();
    const queryClient = useQueryClient();

    const [search, setSearch] = useState(searchParams.get('search') || "");
    const [searchInput, setSearchInput] = useState(searchParams.get('search') || "");
    const [country, setCountry] = useState<string>(searchParams.get('country') || "NL");
    const [province, setProvince] = useState<string>(searchParams.get('province') || "all");
    const [city, setCity] = useState<string>(searchParams.get('city') || "all");
    const [sort, setSort] = useState<string>(searchParams.get('sort') || "popular");

    // Apply search from URL on mount
    useEffect(() => {
        const initialSearch = searchParams.get('search');
        if (initialSearch) {
            setSearch(initialSearch);
            setSearchInput(initialSearch);
        }
    }, []);

    const { data: routes = [], isLoading } = useQuery<any[]>({
        queryKey: ["/api/routes/public", { country, sort }],
        queryFn: async () => {
            const params = new URLSearchParams();
            params.set("country", country);
            params.set("sort", sort);
            const res = await fetch(`/api/routes/public?${params.toString()}`);
            if (!res.ok) throw new Error("Failed to fetch routes");
            return res.json();
        }
    });

    const cities = Array.from(new Set(
        routes
            .filter((r: any) => province === "all" || r.allProvinces?.includes(province) || r.region === province)
            .flatMap((r: any) => r.allCities || (r.city ? [r.city] : []))
    )).sort();

    const filteredRoutes = routes
        .filter((route: any) => {
            if (search) {
                const term = search.toLowerCase();
                const matchesName = route.name.toLowerCase().includes(term);
                const matchesCity = route.allCities?.some((c: string) => c.toLowerCase().includes(term)) || route.city?.toLowerCase().includes(term);
                const matchesShop = route.allShopNames?.some((s: string) => s.toLowerCase().includes(term));

                if (!matchesName && !matchesCity && !matchesShop) {
                    return false;
                }
            }

            if (province !== "all") {
                const matchesProvince = route.allProvinces?.includes(province) || route.region === province;
                if (!matchesProvince) return false;
            }

            if (city !== "all") {
                const matchesCity = route.allCities?.includes(city) || route.city === city;
                if (!matchesCity) return false;
            }

            return true;
        });

    const handleSaveRoute = () => {
        if (!isLoggedIn) {
            setShowLoginModal(true);
        }
    };

    const handleDeleteRoute = async (routeId: string) => {
        try {
            const { data } = await supabase.auth.getSession();
            const token = data.session?.access_token;
            if (!token) return;

            const res = await fetch(`/api/routes/${routeId}`, {
                method: "DELETE",
                headers: { Authorization: `Bearer ${token}` }
            });

            if (!res.ok) throw new Error(t("routeList.deleteFailed"));

            toast({
                title: t("routeList.deletedTitle"),
                description: t("routeList.deletedDesc"),
            });

            queryClient.invalidateQueries({ queryKey: ["/api/routes/public"] });
        } catch (error) {
            toast({
                title: t("routeList.deleteErrorTitle"),
                description: t("routeList.deleteErrorDesc"),
                variant: "destructive",
            });
        }
    };

    return (
        <div className="min-h-screen flex flex-col bg-background">
            

            <main className="flex-1 container mx-auto px-4 py-8">
                <div className="mb-8">
                    <Link href="/routes">
                        <Button variant="ghost" className="mb-4">
                            <ArrowLeft className="h-4 w-4 mr-2" />
                            {t("routeBuilder.backToRoutes")}
                        </Button>
                    </Link>

                    <h1 className="text-2xl md:text-3xl font-bold text-foreground flex items-center gap-2">
                        <Search className="h-6 w-6 text-primary" />
                        {t("routeList.searchTitle")}
                    </h1>
                    {search && (
                        <p className="text-muted-foreground mt-2">
                            {t("routeList.resultsFor", { count: filteredRoutes.length, search })}
                        </p>
                    )}
                </div>

                {/* Search & Filters */}
                <div className="mb-6">
                    <div className="flex flex-col sm:flex-row flex-wrap gap-3">
                        <div className="flex-1 w-full sm:w-auto min-w-[200px] flex gap-2">
                            <Input
                                placeholder={t("routeList.searchPlaceholder")}
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && setSearch(searchInput)}
                                className="w-full"
                                autoFocus
                            />
                            <Button
                                variant="outline"
                                onClick={() => setSearch(searchInput)}
                            >
                                <Search className="h-4 w-4" />
                            </Button>
                        </div>

                        <div className="flex flex-wrap gap-3 w-full sm:w-auto">
                            <Select value={country} onValueChange={(val) => {
                                setCountry(val);
                                setProvince("all");
                                setCity("all");
                            }}>
                                <SelectTrigger className="w-full sm:w-[140px]">
                                    <MapPin className="h-4 w-4 mr-2" />
                                    <SelectValue placeholder={t("shops.country.placeholder")} />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">{t("shops.country.all")}</SelectItem>
                                    {EUROPE_COUNTRIES.map((c) => (
                                        <SelectItem key={c.code} value={c.code}>{c.labelNl}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>

                            <Select value={province} onValueChange={(val) => {
                                setProvince(val);
                                setCity("all");
                            }}>
                                <SelectTrigger className="w-full sm:w-[180px]">
                                    <Map className="h-4 w-4 mr-2" />
                                    <SelectValue placeholder={t("shops.province.placeholder")} />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">{t("shops.province.all")}</SelectItem>
                                    {PROVINCES.map(p => (
                                        <SelectItem key={p} value={p}>{p}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>

                            {province !== "all" && (
                                <Select value={city} onValueChange={setCity}>
                                    <SelectTrigger className="w-full sm:w-[180px]">
                                        <MapPin className="h-4 w-4 mr-2" />
                                        <SelectValue placeholder={t("shops.region.city")} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">{t("routeList.allCities")}</SelectItem>
                                        {cities.map(c => (
                                            <SelectItem key={c} value={c}>{c}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            )}
                        </div>

                        <Select value={sort} onValueChange={(v) => setSort(v as SortOption)}>
                            <SelectTrigger className="w-full sm:w-[150px]">
                                <SortAsc className="h-4 w-4 mr-2" />
                                <SelectValue placeholder={t("shops.sort.placeholder")} />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="popular">{t("shops.sort.popular")}</SelectItem>
                                <SelectItem value="new">{t("routeList.sort.new")}</SelectItem>
                                <SelectItem value="saves">{t("routeList.sort.mostSaved")}</SelectItem>
                                <SelectItem value="stores-desc">{t("routeList.sort.mostShops")}</SelectItem>
                                <SelectItem value="stores-asc">{t("routeList.sort.fewestShops")}</SelectItem>
                                <SelectItem value="distance-desc">{t("routeList.sort.longest")}</SelectItem>
                                <SelectItem value="distance-asc">{t("routeList.sort.shortest")}</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                {/* Results */}
                {isLoading ? (
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((i) => (
                            <div key={i} className="h-[300px] bg-muted/20 animate-pulse rounded-lg" />
                        ))}
                    </div>
                ) : filteredRoutes.length === 0 ? (
                    <div className="text-center py-16">
                        <Search className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                        <h3 className="font-semibold text-lg mb-2">{t("routeList.noRoutesFound")}</h3>
                        <p className="text-muted-foreground mb-4">
                            {search ? t("routeList.noResultsFor", { search }) : t("routeList.enterSearch")}
                        </p>
                        <Button variant="outline" onClick={() => {
                            setSearch("");
                            setSearchInput("");
                            setProvince("all");
                            setCity("all");
                        }}>
                            {t("routeList.clearSearchTerms")}
                        </Button>
                    </div>
                ) : (
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {filteredRoutes.map((route: any) => (
                            <RouteCard
                                key={route.id}
                                route={route}
                                onSave={handleSaveRoute}
                                onDelete={handleDeleteRoute}
                            />
                        ))}
                    </div>
                )}
            </main>

            <LoginModal
                open={showLoginModal}
                onOpenChange={setShowLoginModal}
                action="route"
            />
        </div>
    );
}
